import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import SocialStudio from "@/components/app/SocialStudio";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import DraftsTab from "@/components/app/content-studio/DraftsTab";
import AnalyzeTab from "@/components/app/content-studio/AnalyzeTab";
import GapTab from "@/components/app/content-studio/GapTab";
import DraftEditor from "@/components/app/content-studio/DraftEditor";

/**
 * Content Studio orchestrator. Owns the drafts list + view switching;
 * each tab/editor view is a self-contained child component under
 * `content-studio/`.
 */
export default function ContentStudio({ projectId }) {
  const [view, setView] = useState("list"); // list | editor
  const [drafts, setDrafts] = useState([]);
  const [editorInitial, setEditorInitial] = useState(null);

  const loadDrafts = async () => {
    try {
      const { data } = await api.get(`/projects/${projectId}/content/drafts`);
      setDrafts(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };
  useEffect(() => { loadDrafts(); }, [projectId]);

  const openDraft = (d) => {
    setEditorInitial(d);
    setView("editor");
  };

  const newDraft = () => {
    setEditorInitial({ title: "", target_keyword: "", content: "" });
    setView("editor");
  };

  // Used by Magic Mode + Gap Insert — open editor with a pre-filled skeleton.
  const seedDraft = (seed) => {
    setEditorInitial(seed);
    setView("editor");
  };

  const deleteDraft = async (id) => {
    if (!confirm("Delete this draft?")) return;
    try {
      await api.delete(`/projects/${projectId}/content/drafts/${id}`);
      setDrafts((ds) => ds.filter((d) => d.id !== id));
      toast.success("Draft deleted");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };

  if (view === "editor") {
    return (
      <DraftEditor
        key={editorInitial?.id || "new"}
        projectId={projectId}
        initial={editorInitial}
        onBack={() => setView("list")}
        onSaved={() => loadDrafts()}
      />
    );
  }

  return (
    <Tabs defaultValue="drafts" className="w-full">
      <TabsList className="rounded-none border border-border bg-white p-0 h-auto">
        <TabsTrigger value="drafts" className="rounded-none data-[state=active]:bg-foreground data-[state=active]:text-background px-5 py-2.5" data-testid="content-tab-drafts">Drafts</TabsTrigger>
        <TabsTrigger value="analyze" className="rounded-none data-[state=active]:bg-foreground data-[state=active]:text-background px-5 py-2.5" data-testid="content-tab-analyze">Analyze</TabsTrigger>
        <TabsTrigger value="gap" className="rounded-none data-[state=active]:bg-foreground data-[state=active]:text-background px-5 py-2.5" data-testid="content-tab-gap">Content Gap</TabsTrigger>
        <TabsTrigger value="social" className="rounded-none data-[state=active]:bg-foreground data-[state=active]:text-background px-5 py-2.5" data-testid="content-tab-social">Social Posts</TabsTrigger>
      </TabsList>

      <TabsContent value="drafts">
        <DraftsTab
          projectId={projectId}
          drafts={drafts}
          onOpenDraft={openDraft}
          onNewDraft={newDraft}
          onSeedDraft={seedDraft}
          onDeleteDraft={deleteDraft}
        />
      </TabsContent>

      <TabsContent value="analyze">
        <AnalyzeTab projectId={projectId} />
      </TabsContent>

      <TabsContent value="gap">
        <GapTab projectId={projectId} onSeedDraft={seedDraft} />
      </TabsContent>

      <TabsContent value="social" className="mt-4">
        <SocialStudio mode="auth" projectId={projectId} drafts={drafts} />
      </TabsContent>
    </Tabs>
  );
}
